/**
 * PATH: packages/email-hub/src/feature/components/email-reader/useReaderShortcuts.ts
 * FILE_ID: EMAILHUB-EMAILREADER-SHORTCUTS-HOOK-001
 * PURPOSE: Own reader keyboard navigation and teammate-ping shortcuts.
 */
import { useEffect } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { emailPath } from '../../lib/basePath'
import { siblingEmailIds } from '../../lib/inboxOrder'

interface ReaderShortcutsOptions {
  emailId: string | null
  navigate: NavigateFunction
  onSwitchEmail?: (newEmailId: string) => void
  openPing: () => void
}

function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  const tag = (element?.tagName || '').toLowerCase()
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    element?.isContentEditable === true
  )
}

export function useReaderShortcuts({
  emailId,
  navigate,
  onSwitchEmail,
  openPing,
}: ReaderShortcutsOptions) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '@' || isTypingTarget(event.target)) return
      event.preventDefault()
      openPing()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [openPing])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'j' && event.key !== 'k') return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingTarget(event.target)) return
      const target = event.target as HTMLElement | null
      if (target?.closest('[data-compose-dock]') || !emailId) return

      const { prevId, nextId } = siblingEmailIds(emailId)
      const targetId = event.key === 'j' ? nextId : prevId
      if (!targetId) return
      event.preventDefault()
      if (onSwitchEmail) onSwitchEmail(targetId)
      else navigate(emailPath(`/${targetId}`))
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [emailId, navigate, onSwitchEmail])
}
